import React from 'react';
import PixelIcons from './PixelIcons.jsx';
import { useTweaks } from '../hooks/useTweaks.js';
import { useT } from '../i18n/useT.js';
import { Link, useLocation } from 'react-router-dom';

// Nav.jsx — Top HUD ticker + NavBar (i18n + route-aware, hash links)
const Nav = (() => {
  const { Arrow, Coin, Heart } = PixelIcons;

  const navStyles = {
    wrap: { position: "fixed", top: 0, left: 0, right: 0, zIndex: 100 },
    hud: {
      background: "var(--ragross-purple-night)",
      borderBottom: "1px solid rgba(253,20,145,0.25)",
      fontFamily: "'Press Start 2P', monospace",
      fontSize: "9px", letterSpacing: "0.10em",
      color: "var(--fg-2)",
      overflow: "hidden",
      height: 28,
      display: "flex", alignItems: "center",
    },
    tickerOuter: { flex: 1, overflow: "hidden", position: "relative", height: "100%", display: "flex", alignItems: "center" },
    tickerInner: {
      display: "flex", whiteSpace: "nowrap",
      animation: "hudTickerScroll 38s linear infinite",
      gap: 32, paddingLeft: 32,
    },
    hudFixed: {
      flex: "0 0 auto",
      padding: "0 24px",
      display: "flex", alignItems: "center", gap: 18,
      borderLeft: "1px solid rgba(253,20,145,0.25)",
      height: "100%",
    },
    hudItem: { display: "flex", alignItems: "center", gap: 7 },
    dot: { width: 7, height: 7 },

    nav: {
      background: "rgba(11,0,20,0.82)",
      backdropFilter: "blur(10px)",
      WebkitBackdropFilter: "blur(10px)",
      borderBottom: "1px solid var(--border-soft)",
    },
    navInner: {
      maxWidth: 1280, margin: "0 auto",
      padding: "0 32px", height: 72,
      display: "flex", alignItems: "center", justifyContent: "space-between",
      gap: 24,
    },
    brand: { display: "flex", alignItems: "center", gap: 12, flex: "0 0 auto" },
    brandImg: { height: 48, width: "auto", filter: "drop-shadow(0 0 8px rgba(0,232,203,0.35))" },
    links: { display: "flex", gap: 22, alignItems: "center", flex: 1, justifyContent: "center", flexWrap: "wrap" },
    link: {
      fontFamily: "'Press Start 2P', monospace",
      fontSize: "9px", letterSpacing: "0.10em",
      color: "var(--fg-2)", transition: "color 200ms",
      position: "relative", padding: "6px 0", whiteSpace: "nowrap",
      textDecoration: "none",
    },
    langPill: {
      display: "inline-flex",
      border: "1px solid var(--border-soft)",
      borderRadius: 2, overflow: "hidden",
      fontFamily: "'Press Start 2P', monospace", fontSize: 8,
      flex: "0 0 auto",
    },
    langBtn: (active) => ({
      background: active ? "var(--ragross-cyan)" : "transparent",
      color: active ? "var(--fg-on-cyan)" : "var(--fg-3)",
      border: "none", padding: "8px 10px",
      letterSpacing: "0.16em", cursor: "pointer",
    }),
  };

  function NavLink({ to, label, active }) {
    const [hover, setHover] = React.useState(false);
    return (
      <Link
        to={to}
        style={{ ...navStyles.link, color: hover || active ? "var(--ragross-cyan)" : navStyles.link.color }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        {label}
        <span style={{
          position: "absolute", left: 0, bottom: -2,
          height: 2, width: hover || active ? "100%" : 0,
          background: "var(--ragross-gold)",
          boxShadow: "0 0 8px rgba(253,177,21,0.6)",
          transition: "width 200ms var(--ease-arcade)",
        }}></span>
      </Link>
    );
  }

  function Nav() {
    const [s, setKey] = useTweaks();
    const t = useT();
    const route = useLocation().pathname;

    const items = [
      { to: "/",           label: t("nav.lvl01") },
      { to: "/estudio",    label: t("nav.lvl02") },
      { to: "/servicios",  label: t("nav.lvl03") },
      { to: "/portafolio", label: t("nav.lvl04") },
      { to: "/talleres",   label: t("nav.lvl06") },
      { to: "/tienda",     label: t("nav.lvl07") },
      { to: "/blog",       label: t("nav.lvl08") },
      { to: "/contacto",   label: t("nav.lvl09") },
    ];

    return (
      <header style={navStyles.wrap} data-screen-label="00 Nav">
        {/* HUD ticker ribbon */}
        <div style={navStyles.hud} className="hud-ribbon">
          <div style={navStyles.tickerOuter}>
            <div style={navStyles.tickerInner}>
              {Array.from({ length: 2 }).map((_, i) => (
                <span key={i} style={navStyles.hudItem}>
                  <span style={{ ...navStyles.dot, background: "var(--ragross-green)", boxShadow: "0 0 8px rgba(79,253,21,0.7)" }}></span>
                  <span style={{ color: "var(--fg-3)" }}>{t("hud.active")} ▸ </span>
                  <span style={{ color: "#fff", marginLeft: 8 }}>{t("hud.ticker")}</span>
                </span>
              ))}
            </div>
          </div>
          <div style={navStyles.hudFixed}>
            <span style={navStyles.hudItem}>
              <Coin size={11}/>
              <span style={{ color: "var(--fg-3)" }}>{t("hud.score")}</span>
              <span style={{ color: "#fff" }}>128 500</span>
            </span>
            <span style={navStyles.hudItem}>
              <Heart size={11}/>
              <span style={{ color: "var(--ragross-gold)" }}>04</span>
              <span style={{ color: "var(--fg-3)" }}>{t("hud.activeProjects")}</span>
            </span>
            <span style={navStyles.hudItem}>
              <span style={{ color: "var(--ragross-cyan)" }}>{t("hud.location")}</span>
            </span>
          </div>
        </div>

        {/* Main NavBar */}
        <div style={navStyles.nav}>
          <div style={navStyles.navInner} className="nav-inner">
            <Link to="/" style={navStyles.brand} className="nav-brand">
              <img src="/assets/logos/ragross-primary.png" alt="Ragross Studios" style={navStyles.brandImg}/>
            </Link>
            <nav style={navStyles.links} className="nav-links">
              {items.map((it) => (
                <NavLink key={it.to} to={it.to} label={it.label} active={route === it.to}/>
              ))}
            </nav>
            <div style={{ display: "flex", alignItems: "center", gap: 14, flex: "0 0 auto" }} className="nav-actions">
              <div style={navStyles.langPill}>
                <button style={navStyles.langBtn(s.lang === "es")} onClick={() => setKey("lang", "es")}>ES</button>
                <button style={navStyles.langBtn(s.lang === "en")} onClick={() => setKey("lang", "en")}>EN</button>
              </div>
              <Link to="/contacto" className="btn btn-primary nav-cta">
                <Arrow size={10} color="#fff"/>
                <span>{t("nav.cta")}</span>
              </Link>
            </div>
          </div>
        </div>
      </header>
    );
  }

  return Nav;
})();

export default Nav;
